import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { ImagePlus } from "lucide-react";

/*
  V2.1 · Aufgabe 2 · HEUTE
  - Nur echte Originalfotos vom Breitscheidplatz / Kantstraße erlaubt.
  - Keine KI. Keine Renderings. Keine Vision-Bilder.
  - Solange keine Fotos hochgeladen sind: klare Upload-Slots.
  - Nicht kritisieren. Nicht schlechtreden. Nur Realität.
*/

// 6 Perspektiven. 5 Originalfotos vom User integriert (04.02.2026).
// 1 Slot bleibt offen — der User hat noch 5 weitere Fotos angekündigt.
const TODAY_SLOTS = [
  { key: "breitscheidplatz-wide",  label: "Breitscheidplatz · Weite Perspektive",   aspect: "aspect-[16/10]", span: "col-span-12 md:col-span-8", url: "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/ax9e1xy8_grafik.png" },
  { key: "gedaechtniskirche",      label: "Gedächtniskirche · Vorplatz",            aspect: "aspect-[4/5]",   span: "col-span-12 md:col-span-4", url: "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/td8tme20_grafik.png" },
  { key: "kantstrasse-aerial",    label: "Kantstraße · Provisorische Maßnahmen",   aspect: "aspect-[4/3]",   span: "col-span-12 md:col-span-4", url: "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/et4nmnue_image-1783260218962.jpg" },
  { key: "mobile-sperren",         label: "Mobile Fahrzeugsperren",                 aspect: "aspect-[4/3]",   span: "col-span-12 md:col-span-4", url: "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/0pjfbmj2_WhatsApp%20Image%202026-06-23%20at%2008.09.00%20%283%29.jpeg" },
  { key: "poller-kantstrasse",     label: "Poller",                                 aspect: "aspect-[4/3]",   span: "col-span-12 md:col-span-4", url: "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/p6h1e2c6_grafik.png" },
  { key: "gedaechtniskirche-panorama", label: "Kaiser-Wilhelm-Gedächtniskirche · City West", aspect: "aspect-[16/9]", span: "col-span-12",               url: "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/lp1gozcq_WhatsApp%20Image%202026-06-23%20at%2008.09.00%20%286%29.jpeg" },
];

export default function Today() {
  const { lang } = useLang();
  const copy = {
    de: {
      kicker: "Akt II — Heute",
      title: "Heute.",
      sub: "So schützen wir heute unsere Städte.",
      note: "Originalfotos · Breitscheidplatz & Kantstraße.",
      upload: "Foto benötigt",
    },
    en: {
      kicker: "Act II — Today",
      title: "Today.",
      sub: "This is how we protect our cities today.",
      note: "Original photographs · Breitscheidplatz & Kantstraße.",
      upload: "Photo required",
    },
  }[lang];

  return (
    <section
      id="today"
      data-testid="section-today"
      className="relative bg-[#0F1114] text-white py-32 md:py-48"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-14">
        {/* Header — minimal */}
        <div className="mb-16 md:mb-24 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-white/45 mb-8">
              <span className="h-px w-8 bg-white/40" />
              {copy.kicker}
            </div>
            <h2
              className="font-display font-light tracking-[-0.03em] leading-[0.95] text-white"
              style={{ fontSize: "clamp(3.5rem, 9vw, 8rem)" }}
            >
              {copy.title}
            </h2>
            <p
              className="mt-8 md:mt-10 text-white/70 font-display font-light tracking-[-0.01em] leading-[1.2]"
              style={{ fontSize: "clamp(1.15rem, 1.8vw, 1.75rem)" }}
            >
              {copy.sub}
            </p>
          </motion.div>
        </div>

        {/* Grid — real photos or upload slots */}
        <div className="grid grid-cols-12 gap-4 md:gap-5">
          {TODAY_SLOTS.map((s, i) => (
            <motion.div
              key={s.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`today-slot-${s.key}`}
              className={`relative ${s.span}`}
            >
              <div
                className={`relative ${s.aspect} border border-white/10 overflow-hidden ${
                  s.url ? "" : "bg-[#16191D]"
                }`}
              >
                {s.url ? (
                  <img
                    src={s.url}
                    alt={s.label}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  // Upload placeholder
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6">
                    <div
                      aria-hidden
                      className="absolute inset-0 opacity-[0.08] pointer-events-none"
                      style={{
                        backgroundImage:
                          "linear-gradient(45deg, transparent 48%, rgba(255,255,255,.7) 49%, rgba(255,255,255,.7) 51%, transparent 52%)",
                        backgroundSize: "20px 20px",
                      }}
                    />
                    <ImagePlus className="h-6 w-6 text-white/40" strokeWidth={1.2} />
                    <div className="text-[10px] tracking-[0.35em] uppercase text-white/50 font-display">
                      {copy.upload}
                    </div>
                  </div>
                )}

                {/* Slot label — always visible */}
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-white/70 bg-black/50 backdrop-blur px-3 py-1.5 border border-white/10">
                    <span className="h-1 w-1 rounded-full bg-white/60" />
                    {s.label}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="mt-8 text-[12px] text-white/40 italic max-w-2xl leading-relaxed border-l-2 border-white/20 pl-5"
        >
          {copy.note}
        </motion.p>
      </div>
    </section>
  );
}
